import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import { API_URL } from '../../config/api.ts';
import './ValentineCompatibility.css';

const VERDICTS: { min: number; emoji: string; text: string }[] = [
  { min: 96, emoji: '💘', text: "MATCH PARFAIT ! Cupidon himself est jaloux !! Vous êtes clairement fait l'un pour l'autre 🏹" },
  { min: 81, emoji: '🥰', text: "Âmes sœurs détectées ! L'amour est dans l'air et il sent bon la victoire 💫" },
  { min: 61, emoji: '😍', text: "Wow ! Vous êtes sur la bonne voie ! Encore quelques dates et c'est le grand amour 🌹" },
  { min: 41, emoji: '😊', text: "Pas mal ! Vous avez du potentiel... comme un bon vin, ça se bonifie avec le temps 🍷" },
  { min: 21, emoji: '🤔', text: "Les opposés s'attirent... paraît-il. Au moins vous ne vous ennuierez jamais ensemble 💫" },
  { min: 0, emoji: '💀', text: "C'est pas gagné... Mais l'amour est aveugle, sourd, et un peu naïf ! Foncez quand même 🚀" },
];

const CONFETTI_EMOJIS = ['💕', '💗', '💖', '💘', '💝', '❤️', '🩷', '✨', '🌹', '💐', '🦋', '⭐'];

interface CompatQuestion {
  id: number;
  question: string;
  emoji: string;
  options: { id: string; text: string; emoji: string }[];
}

interface BreakdownItem {
  questionId: number;
  question: string;
  emoji: string;
  myAnswer: string;
  theirAnswer: string;
  match: boolean;
}

const ValentineCompatibility: React.FC = () => {
  const navigate = useNavigate();
  const { code: urlCode } = useParams<{ code?: string }>();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState<CompatQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<'intro' | 'playing' | 'submitting' | 'reveal' | 'result' | 'share'>('intro');
  const [cardKey, setCardKey] = useState(0);
  const [cardExiting, setCardExiting] = useState(false);

  // Comparison mode state
  const [partnerName, setPartnerName] = useState('');
  const [compareCode, setCompareCode] = useState(urlCode || '');
  const [isCompareMode, setIsCompareMode] = useState(!!urlCode);

  // Result state
  const [compatScore, setCompatScore] = useState(0);
  const [breakdown, setBreakdown] = useState<BreakdownItem[]>([]);
  const [myShareCode, setMyShareCode] = useState('');
  const [resultPartnerName, setResultPartnerName] = useState('');
  const [myName, setMyName] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);

  // Unlocked decoration
  const [unlockedDeco, setUnlockedDeco] = useState<string | null>(null);

  // History state
  const [history, setHistory] = useState<any[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<any | null>(null);

  // Auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user: any) => {
      if (user) {
        const t = await user.getIdToken();
        setToken(t);
        if (user.displayName && !myName) setMyName(user.displayName);
      }
    });
    return () => unsub();
  }, []);

  // Load questions + check URL code + history
  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      // If we have a URL code, fetch partner info first to get their questions
      let questionsUrl = `${API_URL}/api/valentine/compatibility/questions`;
      if (urlCode) {
        questionsUrl += `?code=${encodeURIComponent(urlCode)}`;
      }

      const [qRes, hRes] = await Promise.all([
        fetch(questionsUrl),
        fetch(`${API_URL}/api/valentine/compatibility/history`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      ]);
      const data = await qRes.json();
      setQuestions(data.questions || []);

      if (hRes && hRes.ok) {
        const hData = await hRes.json();
        setHistory(hData.history || []);
      }

      // If we have a URL code, fetch partner info
      if (urlCode) {
        const pRes = await fetch(`${API_URL}/api/valentine/compatibility/${urlCode}`);
        if (pRes.ok) {
          const pData = await pRes.json();
          setPartnerName(pData.session?.userName || 'Quelqu\'un');
          setIsCompareMode(true);
          setCompareCode(urlCode);
        }
      }
    } catch (e) {
      console.error('Error loading compat questions:', e);
    } finally {
      setLoading(false);
    }
  }, [token, urlCode]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSelectOption = (optionId: string) => {
    const q = questions[currentIndex];
    if (!q) return;

    const newAnswers = { ...answers, [String(q.id)]: optionId };
    setAnswers(newAnswers);

    // Animate card out, then next
    setTimeout(() => {
      setCardExiting(true);
      setTimeout(() => {
        if (currentIndex < questions.length - 1) {
          setCurrentIndex(prev => prev + 1);
          setCardKey(prev => prev + 1);
          setCardExiting(false);
        } else {
          // All answered
          if (isCompareMode && compareCode) {
            submitCompare(newAnswers);
          } else {
            submitSolo(newAnswers);
          }
        }
      }, 400);
    }, 300);
  };

  // Submit solo (get share code)
  const submitSolo = async (allAnswers: Record<string, string>) => {
    if (!token) return;
    setPhase('submitting');
    try {
      const res = await fetch(`${API_URL}/api/valentine/compatibility/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: allAnswers, questionIds: questions.map(q => q.id) })
      });
      const data = await res.json();
      if (res.ok) {
        setMyShareCode(data.shareCode);
        setPhase('share');
      }
    } catch (e) {
      console.error('Submit error:', e);
    }
  };

  // Submit compare (show compatibility)
  const submitCompare = async (allAnswers: Record<string, string>) => {
    if (!token) return;
    setPhase('submitting');
    try {
      const res = await fetch(`${API_URL}/api/valentine/compatibility/compare`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ shareCode: compareCode, myAnswers: allAnswers })
      });
      const data = await res.json();
      if (res.ok) {
        setCompatScore(data.score);
        setBreakdown(data.breakdown || []);
        setResultPartnerName(data.partnerName || 'Quelqu\'un');
        setMyName(data.myName || 'Toi');
        setMyShareCode(data.myShareCode || '');
        setUnlockedDeco(data.unlockedDecoration || null);
        // Dramatic reveal
        setPhase('reveal');
        setTimeout(() => {
          setPhase('result');
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 4000);
        }, 3500);
      }
    } catch (e) {
      console.error('Compare error:', e);
    }
  };

  const getVerdict = (score: number) => {
    return VERDICTS.find(v => score >= v.min) || VERDICTS[VERDICTS.length - 1];
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/valentine/compatibility/${myShareCode}`;
    navigator.clipboard.writeText(link).then(() => {
      alert('Lien copié ! Envoie-le à ton crush 💌');
    });
  };

  const getOptionText = (qId: number, optId: string) => {
    const q = questions.find(q => q.id === qId);
    if (!q) return optId;
    const opt = q.options.find(o => o.id === optId);
    return opt ? `${opt.emoji} ${opt.text}` : optId;
  };

  if (loading) {
    return (
      <div className="valentine-compat">
        <div className="valentine-loading">
          <div className="valentine-loading-hearts">
            <span>💕</span><span>💗</span><span>💖</span>
          </div>
          <p>Préparation du test de compatibilité...</p>
        </div>
      </div>
    );
  }

  // ═══ INTRO SCREEN ═══
  if (phase === 'intro') {
    return (
      <div className="valentine-compat">
        <div className="valentine-hearts-rain" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}>
          {['💕', '💗', '💖', '💘', '💝', '❤️', '🩷', '✨', '💕', '💗'].map((h, i) => (
            <span key={i} className="valentine-heart-particle" style={{
              position: 'absolute', top: '-50px',
              left: `${(i * 10) + 3}%`,
              fontSize: `${14 + (i % 4) * 4}px`,
              animation: `heartFall ${8 + (i % 5) * 2}s linear ${i * 0.5}s infinite`,
              opacity: 0.4
            }}>{h}</span>
          ))}
        </div>

        <button className="valentine-back-btn" onClick={() => navigate('/valentine')}>
          ← Retour
        </button>

        <div className="valentine-compat-intro">
          <span className="valentine-compat-intro-emoji">💘</span>
          <div className="valentine-compat-intro-title">
            Test de Compatibilité
          </div>
          <div className="valentine-compat-intro-desc">
            {isCompareMode ? (
              <>
                <strong>{partnerName}</strong> t'a envoyé un test de compatibilité !
                <br />Réponds aux 12 questions et découvre si vous êtes faits l'un pour l'autre 💕
              </>
            ) : (
              <>
                Réponds à 12 questions fun et goofy, puis partage ton lien à ton crush !
                <br />Découvrez votre compatibilité amoureuse 💘
              </>
            )}
          </div>

          {isCompareMode && partnerName && (
            <div className="valentine-compat-intro-partner">
              <div className="valentine-compat-intro-partner-name">
                💌 Invitation de {partnerName}
              </div>
              <div className="valentine-compat-intro-partner-label">
                Réponds pour voir votre compatibilité !
              </div>
            </div>
          )}

          {!isCompareMode && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: 'rgba(255,182,193,0.6)', fontSize: '0.85rem', marginBottom: 10 }}>
                Tu as reçu un code ? Entre-le ici :
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
                <input
                  className="valentine-join-input"
                  style={{ width: 180 }}
                  placeholder="Code..."
                  value={compareCode}
                  onChange={(e) => setCompareCode(e.target.value.toUpperCase())}
                  maxLength={9}
                />
                {compareCode.trim() && (
                  <button
                    className="valentine-join-btn"
                    onClick={async () => {
                      try {
                        const code = compareCode.trim();
                        const res = await fetch(`${API_URL}/api/valentine/compatibility/${code}`);
                        if (res.ok) {
                          const data = await res.json();
                          setPartnerName(data.session?.userName || 'Quelqu\'un');
                          setIsCompareMode(true);
                          // Reload questions with partner's set
                          const qRes = await fetch(`${API_URL}/api/valentine/compatibility/questions?code=${encodeURIComponent(code)}`);
                          if (qRes.ok) {
                            const qData = await qRes.json();
                            setQuestions(qData.questions || []);
                          }
                        } else {
                          alert('Code non trouvé 💔');
                        }
                      } catch { alert('Erreur'); }
                    }}
                  >
                    Vérifier
                  </button>
                )}
              </div>
            </div>
          )}

          <button
            className="valentine-compat-start-btn"
            onClick={() => setPhase('playing')}
          >
            {isCompareMode ? '💘 C\'est parti !' : '💕 Commencer le test'}
          </button>

          {/* Historique des tests */}
          {history.length > 0 && (
            <div className="valentine-compat-history">
              <h3 className="valentine-compat-history-title">📋 Mes résultats</h3>
              <div className="valentine-compat-history-list">
                {history.map((h: any, i: number) => {
                  const verdict = getVerdict(h.score);
                  return (
                    <div key={i} className="valentine-compat-history-item" onClick={() => h.breakdown && setSelectedHistory(h)} style={{ cursor: h.breakdown ? 'pointer' : 'default' }}>
                      <div className="valentine-compat-history-couple">
                        <span className="valentine-compat-history-name me">{myName || 'Toi'}</span>
                        <span className="valentine-compat-history-heart">❤️</span>
                        <span className="valentine-compat-history-name partner">{h.partner_name}</span>
                      </div>
                      <div className="valentine-compat-history-right">
                        <span className={`valentine-compat-history-score ${
                          h.score >= 80 ? 'high' : h.score >= 50 ? 'mid' : 'low'
                        }`}>
                          {verdict.emoji} {h.score}%
                        </span>
                        <span className="valentine-compat-history-verdict">
                          {h.score >= 96 ? 'Match parfait' : h.score >= 81 ? 'Âmes sœurs' : h.score >= 61 ? 'Bonne voie' : h.score >= 41 ? 'Potentiel' : h.score >= 21 ? 'Opposés' : 'Duo improbable'}
                        </span>
                        <span className="valentine-compat-history-date">
                          {new Date(h.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Detail modal */}
          {selectedHistory && (
            <div className="valentine-compat-detail-overlay" onClick={() => setSelectedHistory(null)}>
              <div className="valentine-compat-detail-modal" onClick={(e) => e.stopPropagation()}>
                <button className="valentine-compat-detail-close" onClick={() => setSelectedHistory(null)}>✕</button>
                <div className="valentine-compat-detail-header">
                  <span className="valentine-compat-detail-emoji">{getVerdict(selectedHistory.score).emoji}</span>
                  <div className="valentine-compat-detail-names">
                    Toi ❤️ {selectedHistory.partner_name}
                  </div>
                  <div className="valentine-compat-detail-score">{selectedHistory.score}%</div>
                  <div className="valentine-compat-detail-verdict">{getVerdict(selectedHistory.score).text}</div>
                </div>
                <div className="valentine-compat-detail-breakdown">
                  {selectedHistory.breakdown.map((b: any, i: number) => (
                    <div key={i} className={`valentine-compat-breakdown-item ${b.match ? 'match' : 'no-match'}`}>
                      <span>{b.emoji}</span>
                      <div className="valentine-compat-breakdown-q">{b.question}</div>
                      <div style={{ textAlign: 'right', minWidth: 20 }}>
                        {b.match ? '✅' : '❌'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══ SUBMITTING ═══
  if (phase === 'submitting') {
    return (
      <div className="valentine-compat">
        <div className="valentine-loading">
          <div className="valentine-loading-hearts">
            <span>💕</span><span>💗</span><span>💖</span>
          </div>
          <p>{isCompareMode ? 'Cupidon calcule votre compatibilité...' : 'Enregistrement de tes réponses...'}</p>
        </div>
      </div>
    );
  }
  if (phase === 'reveal') {
    return (
      <div className="valentine-compat">
        <div className="valentine-compat-reveal">
          <div className="valentine-compat-reveal-suspense">
            <div style={{ fontSize: 60, marginBottom: 15, animation: 'heartBeat 1.2s ease-in-out infinite' }}>
              💘
            </div>
            <div className="valentine-compat-reveal-title">
              Analyse de compatibilité en cours...
            </div>
            <div className="valentine-love-meter">
              <div
                className="valentine-love-meter-fill"
                style={{ width: `${compatScore}%`, transition: 'width 3s cubic-bezier(0.22, 1, 0.36, 1)' }}
              >
                <span className="valentine-love-meter-label">{compatScore}%</span>
              </div>
            </div>
            <div style={{ color: 'rgba(255,182,193,0.5)', fontSize: '0.8rem', marginTop: 10 }}>
              {resultPartnerName} ❤️ {myName}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══ RESULT ═══
  if (phase === 'result') {
    const verdict = getVerdict(compatScore);
    return (
      <div className="valentine-compat">
        {/* Confetti */}
        {showConfetti && (
          <div className="valentine-confetti">
            {Array.from({ length: 30 }).map((_, i) => (
              <span
                key={i}
                className="valentine-confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`,
                  fontSize: `${12 + Math.random() * 16}px`
                }}
              >
                {CONFETTI_EMOJIS[Math.floor(Math.random() * CONFETTI_EMOJIS.length)]}
              </span>
            ))}
          </div>
        )}

        <button className="valentine-back-btn" onClick={() => navigate('/valentine')}>
          ← Retour
        </button>

        <div className="valentine-compat-result">
          <div className="valentine-compat-result-emoji">{verdict.emoji}</div>
          <div className="valentine-compat-result-names">
            {myName} ❤️ {resultPartnerName}
          </div>
          <div className="valentine-compat-result-score">{compatScore}%</div>
          <div className="valentine-compat-result-label">de compatibilité</div>

          <div className="valentine-compat-result-verdict">
            <div className="valentine-compat-result-verdict-text">
              {verdict.text}
            </div>
          </div>

          {/* Match breakdown */}
          <div className="valentine-compat-breakdown">
            {breakdown.map((b, i) => (
              <div key={i} className={`valentine-compat-breakdown-item ${b.match ? 'match' : 'no-match'}`}>
                <span>{b.emoji}</span>
                <div className="valentine-compat-breakdown-q">{b.question}</div>
                <div style={{ textAlign: 'right', minWidth: 20 }}>
                  {b.match ? '✅' : '❌'}
                </div>
              </div>
            ))}
          </div>

          {/* Decoration unlocked banner */}
          {unlockedDeco && (
            <div className="valentine-compat-unlock-banner">
              <div className="valentine-compat-unlock-icon">
                {unlockedDeco === 'compat_soulmate_2026' ? '💘' : unlockedDeco === 'compat_spark_2026' ? '✨' : '💀'}
              </div>
              <div className="valentine-compat-unlock-text">
                <div className="valentine-compat-unlock-title">🎁 Effet de pseudo débloqué !</div>
                <div className="valentine-compat-unlock-name">
                  {unlockedDeco === 'compat_soulmate_2026'
                    ? 'Amour Fusionnel 💘'
                    : unlockedDeco === 'compat_spark_2026'
                      ? 'Alchimie Naissante ✨'
                      : 'Duo Catastrophe 💀'}
                </div>
                <div className="valentine-compat-unlock-desc">
                  Va dans le gestionnaire d'effets pour l'équiper !
                </div>
              </div>
            </div>
          )}

          {/* Share my code too */}
          {myShareCode && (
            <div className="valentine-compat-share">
              <div className="valentine-compat-share-title">
                📋 Ton code de test
              </div>
              <div className="valentine-compat-share-code">{myShareCode}</div>
              <div className="valentine-compat-share-desc">
                Envoie ton test à d'autres personnes !
              </div>
              <button className="valentine-compat-share-btn" onClick={handleCopyLink}>
                📋 Copier mon lien
              </button>
            </div>
          )}

          <div className="valentine-results-actions" style={{ marginTop: 20 }}>
            <button className="valentine-results-btn" onClick={() => navigate('/valentine')}>
              💕 Retour au hub
            </button>
            <button
              className="valentine-results-btn secondary"
              onClick={() => {
                setPhase('intro');
                setCurrentIndex(0);
                setAnswers({});
                setCompareCode('');
                setIsCompareMode(false);
                setCardKey(0);
                setCardExiting(false);
              }}
            >
              🔄 Refaire le test
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ SHARE (solo mode — no compare) ═══
  if (phase === 'share') {
    return (
      <div className="valentine-compat">
        <div className="valentine-hearts-rain" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}>
          {['💕', '💗', '💖', '💘', '💝', '❤️'].map((h, i) => (
            <span key={i} className="valentine-heart-particle" style={{
              position: 'absolute', top: '-50px',
              left: `${(i * 16) + 5}%`,
              fontSize: `${16 + (i % 3) * 4}px`,
              animation: `heartFall ${9 + (i % 4) * 2}s linear ${i * 0.8}s infinite`,
              opacity: 0.4
            }}>{h}</span>
          ))}
        </div>

        <button className="valentine-back-btn" onClick={() => navigate('/valentine')}>
          ← Retour
        </button>

        <div style={{ maxWidth: 420, margin: '40px auto', textAlign: 'center', position: 'relative', zIndex: 1, animation: 'fadeInUp 0.8s ease both' }}>
          <div style={{ fontSize: 64, marginBottom: 10 }}>💌</div>
          <div style={{ color: '#ffb6c1', fontSize: '1.4rem', fontWeight: 800, marginBottom: 8 }}>
            Test enregistré !
          </div>
          <div style={{ color: 'rgba(255,182,193,0.7)', fontSize: '0.95rem', marginBottom: 25 }}>
            Partage ton code à ton crush pour découvrir votre compatibilité !
          </div>

          <div className="valentine-compat-share">
            <div className="valentine-compat-share-title">Ton code :</div>
            <div className="valentine-compat-share-code">{myShareCode}</div>
            <div className="valentine-compat-share-desc">
              Quand ton crush entre ce code, vous verrez votre % de compatibilité 💘
            </div>
            <button className="valentine-compat-share-btn" onClick={handleCopyLink}>
              📋 Copier le lien
            </button>
          </div>

          <div className="valentine-results-actions" style={{ marginTop: 25 }}>
            <button className="valentine-results-btn" onClick={() => navigate('/valentine')}>
              💕 Retour au hub
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ PLAYING — Question cards ═══
  const question = questions[currentIndex];
  if (!question) return null;

  return (
    <div className="valentine-compat">
      <button className="valentine-back-btn" onClick={() => {
        if (window.confirm('Quitter le test ? Tes réponses seront perdues !')) {
          navigate('/valentine');
        }
      }}>
        ← Quitter
      </button>

      {/* Title */}
      <div className="valentine-title" style={{ marginTop: 10, marginBottom: 15 }}>
        <h1 style={{ fontSize: '1.3rem' }}>💘 Test de Compatibilité</h1>
      </div>

      {/* Question Card */}
      <div className="valentine-compat-question">
        <div
          className={`valentine-compat-card ${cardExiting ? 'valentine-compat-card-exit' : ''}`}
          key={cardKey}
        >
          <span className="valentine-compat-q-emoji">{question.emoji}</span>
          <div className="valentine-compat-q-number">Question {currentIndex + 1} / {questions.length}</div>
          <div className="valentine-compat-q-text">{question.question}</div>

          <div className="valentine-compat-options">
            {question.options.map((opt) => (
              <div
                key={opt.id}
                className={`valentine-compat-option ${answers[String(question.id)] === opt.id ? 'selected' : ''}`}
                onClick={() => handleSelectOption(opt.id)}
              >
                <span className="valentine-compat-option-emoji">{opt.emoji}</span>
                <span className="valentine-compat-option-text">{opt.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Dots indicator */}
        <div className="valentine-compat-dots">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`valentine-compat-dot ${i === currentIndex ? 'active' : i < currentIndex ? 'done' : ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ValentineCompatibility;
